import { Box, Grid } from "@mui/material";
import { FormikProps, getIn, useFormik } from "formik";
import Flex from "@/components/Flex";
import Text from "@/components/Text";
import {
  AUTHORIZATION_EXPIRY,
  CCM_CONSENT_STYLE,
  CCM_PRACTICE_CONTACT,
} from "@/pages/app/provider/consent-forms/ccm-consent/ccm-consent-constant";
import {
  AuthorizationExpiry,
  PracticeContact,
} from "@/pages/app/provider/consent-forms/ccm-consent/ccm-consent-types";
import { PrivacyNoticeAcknowledgement } from "@/pages/app/provider/consent-forms/ccm-consent/components/CcmConsentSections";
import {
  ConsentCheckbox,
  ConsentFieldGrid,
  ConsentSection,
} from "@/pages/app/provider/consent-forms/ccm-consent/components/ConsentFormFields";
import ConsentSignaturePad from "@/pages/app/provider/consent-forms/ccm-consent/components/ConsentSignaturePad";
import {
  ConsentFormSyncOptions,
  ConsentReadOnlyContext,
  useConsentFormSync,
} from "@/pages/app/provider/consent-forms/ccm-consent/hooks/use-consent-form-sync";
import {
  PATIENT_CONSENT_FORM_ID,
  PATIENT_CONSENT_INITIAL_VALUES,
  PATIENT_CONSENT_TEXT,
  PATIENT_RELEASE_ITEM_OPTIONS,
  RELEASE_CONTACT_FIELDS,
  SIGNATORY_FIELDS,
  TREATMENT_CONSENT_ITEMS,
  YES_NO_OPTIONS,
  PATIENT_DEFAULT_EXPIRY_MONTHS,
} from "./patient-consent-constant";
import { patientConsentSchema } from "./patient-consent-schema";
import { LOCKED_PATIENT_FIELDS } from "./patient-consent-utils";
import {
  PatientConsentFormValues,
  PatientReleaseItem,
  TreatmentConsentItem,
} from "./patient-consent-types";
import {
  getConsentSignatureError,
  toDefaultExpiryDate,
} from "@/pages/app/provider/consent-forms/ccm-consent/ccm-consent-utils";
import ConsentExpiryNote from "@/pages/app/provider/consent-forms/ccm-consent/components/ConsentExpiryNote";
import { EXPIRY_FIELD_NAME } from "@/pages/app/provider/consent-forms/ccm-consent/ccm-consent-copy-constant";

interface TreatmentConsentRowProps {
  formik: FormikProps<PatientConsentFormValues>;
  item: TreatmentConsentItem;
}

const TreatmentConsentRow = ({ formik, item }: TreatmentConsentRowProps) => {
  const { answerField, text, note } = item;
  const answer: string = answerField ? getIn(formik.values, answerField) : "";

  return (
    <Flex alignItems="center" flexWrap="wrap" columnGap={2}>
      <Text textAlign="justify">{text}</Text>
      {answerField &&
        YES_NO_OPTIONS.map((option) => (
          <ConsentCheckbox
            key={option.value}
            label={option.label}
            checked={answer === option.value}
            onChange={(isChecked) =>
              formik.setFieldValue(answerField, isChecked ? option.value : "")
            }
          />
        ))}
      {note && <Text>{note}</Text>}
    </Flex>
  );
};

export { patientConsentSchema };

export interface PatientConsentFormProps extends ConsentFormSyncOptions<PatientConsentFormValues> {
  onSubmit?: (values: PatientConsentFormValues) => void | Promise<void>;
  initialValues?: PatientConsentFormValues;
  practiceContact?: PracticeContact;
  formId?: string;
  readOnly?: boolean;
}

const PatientConsentForm = ({
  onSubmit,
  initialValues = PATIENT_CONSENT_INITIAL_VALUES,
  practiceContact = CCM_PRACTICE_CONTACT,
  formId = PATIENT_CONSENT_FORM_ID,
  readOnly = false,
  onValuesChange,
  submitCount,
}: PatientConsentFormProps) => {
  const formik = useFormik<PatientConsentFormValues>({
    initialValues,
    validationSchema: patientConsentSchema,
    enableReinitialize: true,
    onSubmit: (values) => onSubmit?.(values),
  });
  useConsentFormSync(formik, { onValuesChange, submitCount });
  const { releaseAuthorization, signature } = formik.values;
  const { expiry, releaseItems } = releaseAuthorization;

  const selectExpiry = (value: AuthorizationExpiry, isChecked: boolean) => {
    formik.setFieldValue("releaseAuthorization.expiry", isChecked ? value : "");
    formik.setFieldValue(
      "releaseAuthorization.expiryDate",
      isChecked && value === AUTHORIZATION_EXPIRY.DATE
        ? toDefaultExpiryDate(PATIENT_DEFAULT_EXPIRY_MONTHS)
        : null
    );
  };

  const toggleReleaseItem = (item: PatientReleaseItem, isChecked: boolean) =>
    formik.setFieldValue(
      "releaseAuthorization.releaseItems",
      isChecked
        ? [...releaseItems, item]
        : releaseItems.filter((selected) => selected !== item)
    );

  return (
    <ConsentReadOnlyContext.Provider value={readOnly}>
      <form id={formId} noValidate onSubmit={formik.handleSubmit}>
        <Flex flexDirection="column" gap={2}>
          <Text
            component="h2"
            variant="h5"
            textAlign="center"
            fontWeight={CCM_CONSENT_STYLE.HEADING_WEIGHT}
            color={CCM_CONSENT_STYLE.HEADER_TEXT}
          >
            {PATIENT_CONSENT_TEXT.TITLE}
          </Text>

          <Box
            p={1.5}
            sx={{
              border: 1,
              borderColor: CCM_CONSENT_STYLE.BORDER,
              borderRadius: 1,
            }}
          >
            <ConsentFieldGrid formik={formik} fields={LOCKED_PATIENT_FIELDS} />
          </Box>

          <ConsentSection title={PATIENT_CONSENT_TEXT.RELEASE_AUTHORIZATION}>
            <ConsentFieldGrid formik={formik} fields={RELEASE_CONTACT_FIELDS} />
            <Flex alignItems="center" flexWrap="wrap" columnGap={2} rowGap={1}>
              <Text>{PATIENT_CONSENT_TEXT.AUTHORIZATION_EXPIRES}</Text>
              <Flex alignItems="center" gap={1}>
                <ConsentCheckbox
                  label={PATIENT_CONSENT_TEXT.NEVER}
                  checked={expiry === AUTHORIZATION_EXPIRY.NEVER_EXPIRES}
                  onChange={(isChecked) =>
                    selectExpiry(AUTHORIZATION_EXPIRY.NEVER_EXPIRES, isChecked)
                  }
                />
                <ConsentCheckbox
                  label={PATIENT_CONSENT_TEXT.DATE}
                  checked={expiry === AUTHORIZATION_EXPIRY.DATE}
                  onChange={(isChecked) =>
                    selectExpiry(AUTHORIZATION_EXPIRY.DATE, isChecked)
                  }
                />
                <Text variant="caption" color="error">
                  {getConsentSignatureError(formik, EXPIRY_FIELD_NAME)}
                </Text>
              </Flex>
            </Flex>
            <ConsentExpiryNote
              expiry={expiry}
              expiryDate={releaseAuthorization.expiryDate}
              defaultMonths={PATIENT_DEFAULT_EXPIRY_MONTHS}
            />
            <Flex alignItems="center" flexWrap="wrap" columnGap={2}>
              <Text fontWeight={CCM_CONSENT_STYLE.HEADING_WEIGHT}>
                {PATIENT_CONSENT_TEXT.RELEASE_FOLLOWING}
              </Text>
              {PATIENT_RELEASE_ITEM_OPTIONS.map((option) => (
                <ConsentCheckbox
                  key={option.value}
                  label={option.label}
                  checked={releaseItems.includes(option.value)}
                  onChange={(isChecked) =>
                    toggleReleaseItem(option.value, isChecked)
                  }
                />
              ))}
            </Flex>
          </ConsentSection>

          <ConsentSection title={PATIENT_CONSENT_TEXT.TREATMENT_CONSENT}>
            {TREATMENT_CONSENT_ITEMS.map((item) => (
              <TreatmentConsentRow key={item.id} formik={formik} item={item} />
            ))}
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <ConsentSignaturePad
                  label={PATIENT_CONSENT_TEXT.PATIENT_SIGNATURE}
                  required={!signature.unableToSignReason?.trim()}
                  error={getConsentSignatureError(
                    formik,
                    "signature.patientSignature"
                  )}
                  value={signature.patientSignature}
                  onChange={(value) =>
                    formik.setFieldValue("signature.patientSignature", value)
                  }
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <ConsentSignaturePad
                  label={PATIENT_CONSENT_TEXT.RESPONSIBLE_SIGNATURE}
                  required={!!signature.unableToSignReason?.trim()}
                  error={getConsentSignatureError(
                    formik,
                    "signature.responsiblePersonSignature"
                  )}
                  value={signature.responsiblePersonSignature}
                  onChange={(value) =>
                    formik.setFieldValue(
                      "signature.responsiblePersonSignature",
                      value
                    )
                  }
                />
              </Grid>
            </Grid>
            <ConsentFieldGrid formik={formik} fields={SIGNATORY_FIELDS} />
          </ConsentSection>

          <PrivacyNoticeAcknowledgement
            formik={formik}
            practiceContact={practiceContact}
          />
        </Flex>
      </form>
    </ConsentReadOnlyContext.Provider>
  );
};

export default PatientConsentForm;
